import React, { useEffect, useRef, useState } from "react";
import { getEvidenceSamples, requestMoreEvidenceSamples } from "../../services/antiFraudService";

const MAX_FRAMES = 24;

function stripMediaFragment(src) {
  return String(src || "").split("#")[0];
}

function formatClock(seconds) {
  const total = Math.max(0, Math.floor(seconds || 0));
  const mins = Math.floor(total / 60);
  const secs = String(total % 60).padStart(2, "0");
  return `${mins}:${secs}`;
}

function EvidenceSampleStrip({ sessionId, kind, src, label }) {
  const playerRef = useRef(null);
  const [frames, setFrames] = useState([]);
  const [status, setStatus] = useState(src ? "loading" : "missing");
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let pollTimer = null;

    if (!src) {
      setFrames([]);
      setStatus("missing");
      return undefined;
    }
    if (!sessionId || !kind) {
      setFrames([]);
      setStatus("ready");
      return undefined;
    }

    const load = (isPoll = false, count) => {
      if (!isPoll) setStatus("loading");
      return getEvidenceSamples(sessionId, kind, count)
        .then((payload) => {
          if (cancelled) return;
          const nextFrames = (payload?.[kind] || []).filter((frame) => frame?.url);
          setFrames(nextFrames);
          if (nextFrames.length > 0 && !payload?.pending) {
            setStatus("ready");
            return;
          }
          if (payload?.pending) {
            setStatus("generating");
            pollTimer = setTimeout(() => load(true, count), 5000);
            return;
          }
          setStatus("ready");
        })
        .catch(() => {
          if (cancelled) return;
          setFrames([]);
          setStatus("ready");
        });
    };

    load(false);

    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, [sessionId, kind, src]);

  const jumpTo = (seconds) => {
    const player = playerRef.current;
    if (!player) return;
    player.currentTime = Math.max(0, seconds);
    player.play?.().catch(() => {});
  };

  const handleAddFrames = async () => {
    if (!sessionId || !kind || adding || frames.length >= MAX_FRAMES) return;
    setAdding(true);
    setError(null);
    setStatus("generating");
    try {
      const payload = await requestMoreEvidenceSamples(sessionId, { kind, add: 5 });
      const nextFrames = (payload?.[kind] || []).filter((frame) => frame?.url);
      setFrames(nextFrames);
      if (payload?.pending) {
        setStatus("generating");
        // Poll until ready
        const poll = async () => {
          const again = await getEvidenceSamples(sessionId, kind, nextFrames.length || payload.count);
          const refreshed = (again?.[kind] || []).filter((frame) => frame?.url);
          setFrames(refreshed);
          if (again?.pending) {
            setTimeout(poll, 5000);
          } else {
            setStatus("ready");
          }
        };
        setTimeout(poll, 5000);
      } else {
        setStatus("ready");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Could not generate more frames");
      setStatus("ready");
    } finally {
      setAdding(false);
    }
  };

  if (!src) {
    return (
      <div className="sample-strip">
        <h4>{label}</h4>
        <span className="video-missing">No video in the session bucket folder</span>
      </div>
    );
  }

  const isBusy = status === "loading" || status === "generating" || adding;
  const canAddMore = frames.length > 0 && frames.length < MAX_FRAMES && !isBusy;

  return (
    <div className="sample-strip">
      <div className="sample-strip-header">
        <h4>{label}</h4>
        {canAddMore && (
          <button
            type="button"
            className="sample-add-frames"
            onClick={handleAddFrames}
            disabled={adding}
          >
            +5 frames
          </button>
        )}
      </div>
      {isBusy && (
        <div className="sample-loading" role="status" aria-live="polite">
          <div className="sample-spinner" aria-hidden="true" />
          <p className="sample-status">
            {status === "generating" || adding
              ? "Generating review stills from this video…"
              : "Loading review stills…"}
          </p>
          <div className="sample-skeleton-grid" aria-hidden="true">
            {[0, 1, 2].map((slot) => (
              <div key={slot} className="sample-skeleton-card" />
            ))}
          </div>
        </div>
      )}
      {error && <p className="sample-status sample-error">{error}</p>}
      {frames.length > 0 && (
        <div className="sample-grid">
          {frames.map((frame) => (
            <button
              key={`${frame.label}-${frame.seconds}`}
              type="button"
              className="sample-card"
              onClick={() => jumpTo(frame.seconds)}
            >
              <img src={frame.url} alt={`${label} ${frame.label}`} />
              <span>
                {frame.label} · {formatClock(frame.seconds)}
              </span>
            </button>
          ))}
        </div>
      )}
      {status === "ready" && frames.length === 0 && (
        <p className="sample-status">
          Could not generate stills for this video. Use the player or open the full video.
        </p>
      )}
      <div className="sample-player-wrap">
        <video
          ref={playerRef}
          src={stripMediaFragment(src)}
          controls
          playsInline
          className="sample-player"
        />
        {frames.length > 0 && (
          <div className="sample-jumps">
            {frames.map((frame) => (
              <button
                key={`jump-${frame.label}-${frame.seconds}`}
                type="button"
                className="sample-jump"
                onClick={() => jumpTo(frame.seconds)}
              >
                Jump to {frame.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default EvidenceSampleStrip;
